import { GetRowIdParams, GridReadyEvent } from '@ag-grid-community/core';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AgFns, GridOptionsExt, ISortModel, ProxAgFns, RowSelectionMultipleCheckbox, UtilDialogService, wrapBusy } from '@core';
import { EntityFns } from '@data';
import { ProgramRapidTemplate, ProgramRapidTemplateProductTypeConfig } from '@models';
import { Entity } from 'breeze-client';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'primeng/dynamicdialog';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';
import { ProductTypeConfigFinderDialog } from './product-type-config.finder.dialog';
import { ProgramFrmComponent } from './program-frm.component';

@Component({
  selector: 'prox-program-rapid-templates-sub',
  templateUrl: './program-rapid-templates.sub.component.html',
  standalone: false,
})
export class ProgramRapidTemplatesSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  rtGridOptions: GridOptionsExt = {};
  rapidTemplates?: ProgramRapidTemplate[];

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
    public pngDialogService: DialogService,
    public toastr: ToastrService,
  ) {
    //
  }

  get program() {
    return this.parent.program;
  }

  async ngOnInit() {
    this.rtGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onRtGridReady,
        rowModelType: 'clientSide',
      },
      { detailProperty: 'programRapidTemplateProductTypeConfigs' },
    );
    this.updateRoMasterDetail(this.rtGridOptions);

    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  @wrapBusy
  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);

    const isLoaded = this.program.entityAspect.isNavigationPropertyLoaded('programRapidTemplates');
    if (isLoaded && !forceRefresh) {
      this.rapidTemplates = this.program.programRapidTemplates;
    } else {
      this.rapidTemplates = await this.dbQueryService.getRapidOrders(this.program.id);

      this.program.entityAspect.markNavigationPropertyAsLoaded('programRapidTemplates');
    }

    // needed to refresh colDefs based on any changes
    this.onRtGridReady();
    await AgFns.refreshGrid(this.rtGridOptions, this.rapidTemplates);

    AgFns.sizeColumnsToFit(this.rtGridOptions);

    AgFns.selectDefaultRow(this.rtGridOptions);
  }

  onRtGridReady(event?: GridReadyEvent) {
    const colDefs = [
      ProxAgFns.getEntityStateColDef(),
      {
        headerName: 'Name',
        field: 'name',
        filter: 'agTextColumnFilter',
        editable: !this.parent.isReadOnly(),
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        headerName: 'Description',
        field: 'description',
        editable: !this.parent.isReadOnly(),
        filter: 'agTextColumnFilter',
      },
      this.parent.ifEditable({
        ...AgFns.createButtonProps('', this.onRapidTemplateAttach.bind(this), {
          colId: 'attachProducts',
          label: 'Attach Products',
        }),
        minWidth: 150,
        maxWidth: 150,
      }),
      this.parent.ifEditable(ProxAgFns.getEntityDeleteColDef(this.onRoDelete.bind(this))),
    ];
    const sortModel: ISortModel = [{ colId: 'name', sort: 'asc' }];

    AgFns.initGrid(event ?? this.rtGridOptions, colDefs, sortModel);
  }

  updateRoMasterDetail(parentGridOptions: GridOptionsExt) {
    parentGridOptions.masterDetail = true;
    const detailGridOptions = AgFns.createDetailGridOptions();
    const prodTypePath = 'productTypeConfig.pricedProductType.productType';
    detailGridOptions.columnDefs = [
      {
        headerName: 'Supplier',
        field: prodTypePath + '.supplier.name',
        filter: 'agTextColumnFilter',
        cellRenderer: 'agGroupCellRenderer',
        maxWidth: 200,
      },
      {
        headerName: 'Manufacturer',
        field: prodTypePath + '.manufacturer.name',
        filter: 'agTextColumnFilter',
      },
      // {
      //   headerName: 'SKU',
      //   field: prodTypePath + '.productKey',
      //   filter: 'agTextColumnFilter',
      // },
      {
        headerName: 'Config / Catalog',
        field: 'productTypeConfig.name',
        filter: 'agTextColumnFilter',
        maxWidth: 200,
      },
      {
        headerName: 'Product Name',
        field: prodTypePath + '.name',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Description',
        field: prodTypePath + '.description',
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'Qty',
        editable: true,
        field: 'qty',
        maxWidth: 100,
      },
      {
        ...AgFns.createButtonProps('', this.onRapidTemplateDetach.bind(this), {
          label: 'Detach Product',
        }),
        minWidth: 150,
        maxWidth: 150,
      },
    ];
    detailGridOptions.getRowId = (rowIdParams: GetRowIdParams) => {
      const x = rowIdParams.data as ProgramRapidTemplateProductTypeConfig;
      return x.productTypeConfigId;
    };
    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      refreshStrategy: 'everything',

      getDetailRowData: (params) => {
        const rt = params.data as ProgramRapidTemplate;
        params.successCallback(rt.programRapidTemplateProductTypeConfigs);
      },
    };
  }

  // async handleRapidOrders() {
  //   if (this.isBeingAdded) {
  //     this.rapidOrders = this.rapidOrders ?? [];
  //   } else if (this.rapidOrders == null) {
  //     this.rapidOrders = await this.dbQueryService.getRapidOrders(
  //       this.program.id
  //     );
  //     // rapid orders reference productTypeConfigs.
  //     await this.ensureRapidOrderDetails();
  //   }
  //   AgFns.refreshGrid(this.roGridOptions, this.rapidOrders, true);
  //   AgFns.selectFirstRow(this.roGridOptions);
  // }

  // async ensureRapidOrderDetails() {
  //   if (this.programProductTypeConfigs == null) {
  //     this.programProductTypeConfigs =
  //       await this.dbQueryService.getProgramProductTypeConfigs(this.program.id);
  //     this.calcPaFeatures();
  //   }
  // }

  async onRtAdd() {
    if (this.rapidTemplates == null) return;

    const newView = this.dbSaveService.createEntity(ProgramRapidTemplate, {
      programId: this.program.id,
    });
    const x = newView.programRapidTemplateProductTypeConfigs;
    this.rapidTemplates.push(newView);
    AgFns.refreshGrid(this.rtGridOptions, this.rapidTemplates);
  }

  async onRoDelete(ro: ProgramRapidTemplate) {
    if (!this.rapidTemplates) return;
    ro.programRapidTemplateProductTypeConfigs.slice().forEach((x) => EntityFns.deleteOrDetach(x.entityAspect));
    EntityFns.deleteOrDetach(ro.entityAspect);
    _.remove(this.rapidTemplates, ro);
    AgFns.refreshGrid(this.rtGridOptions, this.rapidTemplates);
  }

  async onRapidTemplateAttach(row: ProgramRapidTemplate) {
    const existingPtcIds = new Set(row.programRapidTemplateProductTypeConfigs.map((x) => x.productTypeConfigId));
    const finderPtcs = this.program.programProductTypeConfigs?.map((x) => x.productTypeConfig).filter((x) => !existingPtcIds.has(x.id));
    const newPtcs = await this.dialogService.createFinder(ProductTypeConfigFinderDialog, {
      productTypeConfigs: finderPtcs,
      rowSelection: RowSelectionMultipleCheckbox,
    });
    newPtcs.forEach((ptc) => {
      if (!this.dbSaveService.uow.undoIfDeleted(ProgramRapidTemplateProductTypeConfig, [row.id, ptc.id])) {
        this.dbSaveService.createEntity(ProgramRapidTemplateProductTypeConfig, {
          programRapidTemplateId: row.id,
          productTypeConfigId: ptc.id,
        });
      }
    });
    // HACK - AgGrid issue - complete hack - needed to show expando on grid  if row doesn't already have it.
    AgFns.refreshGrid(this.rtGridOptions, []);
    AgFns.refreshGrid(this.rtGridOptions, this.rapidTemplates);

    const rowNode = this.rtGridOptions.apiX?.getRowNode(row.id);
    if (rowNode) {
      rowNode?.setExpanded(true);
      this.rtGridOptions.apiX?.redrawRows({ rowNodes: [rowNode] });
    }
    AgFns.refreshDetailRowHeights(this.rtGridOptions);
  }

  async onRapidTemplateDetach(row: ProgramRapidTemplateProductTypeConfig) {
    if (this.rapidTemplates == null) return;
    const parentRowNode = this.rtGridOptions.apiX?.getRowNode(row.programRapidTemplateId);
    EntityFns.deleteOrDetach(row.entityAspect);

    AgFns.refreshGrid(this.rtGridOptions, this.rapidTemplates);
    parentRowNode?.setExpanded(true);
    AgFns.refreshDetailRowHeights(this.rtGridOptions);
  }

  async markError(errEnt: Entity, propName?: string) {
    const ent = errEnt as ProgramRapidTemplate;
    AgFns.selectGridRowByKey(this.rtGridOptions, (e: ProgramRapidTemplate) => e.id, ent.id, propName);
  }
}
